import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/dateUtils';
import ReportWhatsAppModal from '../../components/ReportWhatsAppModal';

const UnifiedStatement = () => {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [searchParams] = useSearchParams();
  const defaultAccountId = searchParams.get('accountId') || '';

  const [accounts, setAccounts] = useState([]);
  const [firms, setFirms] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState(isAdmin ? 'ALL' : (user?.firmId || '1'));
  const [selectedAccountId, setSelectedAccountId] = useState(defaultAccountId);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [accRes, configRes] = await Promise.all([
          axiosClient.get('/accounts'),
          axiosClient.get('/auth/config')
        ]);
        if (accRes.success) {
          setAccounts(accRes.data || []);
          if (!selectedAccountId && accRes.data?.length > 0) {
            setSelectedAccountId(accRes.data[0].Id);
          }
        }
        if (configRes.success) {
          setFirms(configRes.data?.firms || []);
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchInitialData();
  }, []);

  const handleFetchStatement = async () => {
    if (!selectedAccountId) return;
    setLoading(true);
    try {
      const firmParam = isAdmin ? (selectedFirm || 'ALL') : (user?.firmId || '');
      const res = await axiosClient.get(
        `/reports/statement?accountId=${selectedAccountId}&fromDate=${fromDate}&toDate=${toDate}&firmId=${firmParam}`
      );
      if (res.success) {
        setStatement(res.data);
      }
    } catch (err) {
      alert(err.message || 'Error loading account statement.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedAccountId) {
      handleFetchStatement();
    }
  }, [selectedAccountId, selectedFirm]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="container-fluid p-0">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2 no-print">
        <div>
          <h3 className="fw-bold mb-0 text-dark">
            <i className="bi bi-journal-text text-primary me-2"></i> Unified Account Statement (Ledger)
          </h3>
          <p className="text-muted small mb-0">
            {isAdmin 
              ? 'Multi-Firm Admin Ledger: Combined or branch-wise customer/supplier running balance statement' 
              : `Account Statement for: ${user?.firmName || 'Assigned Branch'}`}
          </p>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-success d-flex align-items-center gap-2"
            onClick={() => setShowWhatsAppModal(true)}
            disabled={!statement}
          >
            <i className="bi bi-whatsapp"></i> Send WhatsApp
          </button>
          <button className="btn btn-primary d-flex align-items-center gap-2" onClick={handlePrint} disabled={!statement}>
            <i className="bi bi-printer"></i> Print Statement
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="card shadow-sm border-0 mb-4 no-print">
        <div className="card-body p-3">
          <div className="row g-2 align-items-end">
            {isAdmin ? (
              <div className="col-12 col-md-3">
                <label className="form-label small fw-semibold text-muted">
                  <i className="bi bi-building text-warning me-1"></i> Firm Branch
                </label>
                <select
                  className="form-select"
                  value={selectedFirm}
                  onChange={(e) => setSelectedFirm(e.target.value)}
                >
                  <option value="ALL">★ All Firms (Consolidated)</option>
                  {firms.map((f) => (
                    <option key={f.Id} value={f.Id}>
                      {f.firmName}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="col-12 col-md-3">
                <label className="form-label small fw-semibold text-muted">
                  <i className="bi bi-building text-warning me-1"></i> Your Branch
                </label>
                <div className="form-control bg-light text-dark fw-semibold small text-truncate">
                  {user?.firmName || 'Assigned Firm'}
                </div>
              </div>
            )}

            <div className="col-12 col-md-3">
              <label className="form-label small fw-semibold text-muted">Select Party / Account *</label>
              <select
                className="form-select fw-bold"
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
              >
                {accounts.map((a) => (
                  <option key={a.Id} value={a.Id}>
                    {a.AccountName} ({a.AccountType})
                  </option>
                ))}
              </select>
            </div>

            <div className="col-6 col-md-2">
              <label className="form-label small fw-semibold text-muted">From Date</label>
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="col-6 col-md-2">
              <label className="form-label small fw-semibold text-muted">To Date</label>
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="col-12 col-md-2">
              <button className="btn btn-ocean w-100" onClick={handleFetchStatement}>
                Generate
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Statement Sheet */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      ) : statement ? (
        <div className="card shadow-sm border-0 print-area">
          <div className="card-body p-4">
            {/* Header details */}
            <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-3">
              <div>
                <h4 className="fw-bold mb-0 text-primary">{statement.account?.AccountName}</h4>
                <div className="text-muted small">
                  Type: <strong>{statement.account?.AccountType}</strong> | Mobile: {statement.account?.MobileNo || '-'}
                </div>
                <div className="text-muted small">Address: {statement.account?.Address || '-'}</div>
              </div>
              <div className="text-end">
                <h5 className="fw-bold mb-1 text-dark">ACCOUNT STATEMENT</h5>
                <div className="text-muted small">
                  Period: <strong>{formatDate(statement.fromDate)}</strong> to <strong>{formatDate(statement.toDate)}</strong>
                </div>
                <div className="text-muted small">Generated on: {formatDate(new Date())}</div>
              </div>
            </div>

            {/* Opening Balance Row */}
            <div className="alert alert-light border d-flex justify-content-between align-items-center py-2 px-3 mb-3">
              <strong>Opening Balance (as of {formatDate(statement.fromDate)}):</strong>
              <span className={`fw-bold fs-6 ${statement.openingBalance >= 0 ? 'text-primary' : 'text-danger'}`}>
                ₹{parseFloat(statement.openingBalance).toFixed(2)}
              </span>
            </div>

            {/* Ledger Transactions Table */}
            <div className="table-responsive">
              <table className="table table-bordered table-sm align-middle mb-3 small">
                <thead className="table-light text-center">
                  <tr>
                    <th style={{ width: '90px' }}>Date</th>
                    <th style={{ width: '90px' }}>Type</th>
                    {statement.isAllFirms && <th style={{ width: '130px' }}>Firm</th>}
                    <th>Particulars / Description</th>
                    <th className="text-end" style={{ width: '130px' }}>Debit (+) (₹)</th>
                    <th className="text-end" style={{ width: '130px' }}>Credit (-) (₹)</th>
                    <th className="text-end" style={{ width: '140px' }}>Running Bal (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {statement.transactions.length === 0 ? (
                    <tr>
                      <td colSpan={statement.isAllFirms ? 7 : 6} className="text-center py-4 text-muted">
                        No transactions found in this date range.
                      </td>
                    </tr>
                  ) : (
                    statement.transactions.map((t, idx) => (
                      <tr key={idx}>
                        <td className="text-center">{formatDate(t.date)}</td>
                        <td className="text-center">
                          <span
                            className={`badge ${
                              t.type === 'SALE'
                                ? 'bg-primary'
                                : t.type === 'PURCHASE'
                                ? 'bg-warning text-dark'
                                : t.type === 'RECEIPT'
                                ? 'bg-success'
                                : 'bg-danger'
                            }`}
                          >
                            {t.type}
                          </span>
                        </td>
                        {statement.isAllFirms && (
                          <td className="text-center">
                            <span className="badge bg-light text-secondary border">{t.firmName || '-'}</span>
                          </td>
                        )}
                        <td>
                          <strong>{t.description}</strong>
                          {t.remark && <div className="text-muted small">{t.remark}</div>}
                        </td>
                        <td className="text-end fw-semibold text-primary">
                          {t.debit > 0 ? `₹${parseFloat(t.debit).toFixed(2)}` : '-'}
                        </td>
                        <td className="text-end fw-semibold text-success">
                          {t.credit > 0 ? `₹${parseFloat(t.credit).toFixed(2)}` : '-'}
                        </td>
                        <td className={`text-end fw-bold ${t.runningBalance >= 0 ? 'text-dark' : 'text-danger'}`}>
                          ₹{parseFloat(t.runningBalance).toFixed(2)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Closing Balance Summary */}
            <div className="row justify-content-end">
              <div className="col-12 col-md-5">
                <table className="table table-sm table-bordered">
                  <tbody>
                    <tr>
                      <td className="text-muted">Total Debits (Sales/Charges):</td>
                      <td className="text-end fw-bold text-primary">₹{parseFloat(statement.totalDebit || 0).toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Total Credits (Receipts/Purchases):</td>
                      <td className="text-end fw-bold text-success">₹{parseFloat(statement.totalCredit || 0).toFixed(2)}</td>
                    </tr>
                    <tr className="table-dark fs-6">
                      <td className="fw-bold">Closing Balance:</td>
                      <td className="text-end fw-bold">₹{parseFloat(statement.closingBalance || 0).toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* WhatsApp Sharing Modal */}
      {showWhatsAppModal && statement && (
        <ReportWhatsAppModal
          show={showWhatsAppModal}
          onClose={() => setShowWhatsAppModal(false)}
          title={`Statement - ${statement.account?.AccountName}`}
          reportType="statement"
          reportData={{
            ...statement,
            firmName: user?.firmName || 'PUSHPAI SEA FOOD(ALIBAG)'
          }}
        />
      )}
    </div>
  );
};

export default UnifiedStatement;
